import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../components/LayoutWrapper";
import HeaderBar from "../components/HeaderBar";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaDownload,
  FaPrint,
  FaFileInvoiceDollar,
  FaEye,
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";

const API_BASE = "http://nissi-india.com/api";

const HOBillPassReport = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [loading, setLoading] = useState(false);

  // Filters
  const [branch, setBranch] = useState("8");
  const [supplier, setSupplier] = useState("");
  const [bpNo, setBpNo] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Dropdown / Data
  const [branchesList, setBranchesList] = useState([]);
  const [suppliersList, setSuppliersList] = useState([]);
  const [results, setResults] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);

  // localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "8";
  const branchType = (localStorage.getItem("branchType") || "HO").toUpperCase();
  const userId = localStorage.getItem("userId") || "";
  const loginUserId = localStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
      "x-loginuserid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, userId, loginUserId]
  );

  const formatDateForApi = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}/${month}/${year}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? dateStr : date.toLocaleDateString("en-GB");
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === "") return "-";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount || 0));
  };

  const clearFilters = () => {
    setBranch(branchId || "8");
    setSupplier("");
    setBpNo("");
    setFromDate("");
    setToDate("");
    setResults([]);
    setGrandTotal(0);
  };

  const handleExport = () => {
    if (!results.length) {
      toast.warn("No data available to export");
      return;
    }

    const headers = [
      "Bill Pass No",
      "Date",
      "Supplier",
      "Branch",
      "Amount",
    ];

    const rows = results.map((item) => [
      item.fld_BillPassNo || "",
      item.fld_EntryDate || "",
      item.fld_suppliername || "",
      item.branchname || "",
      item.fld_TotalValueNew || item.fld_TotalValue || "",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((col) => `"${String(col).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "ho_bill_pass_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchBranches = async () => {
    try {
      const res = await axios.get(`${API_BASE}/bindddl_rpt`, {
        params: {
          branchtype: branchType === "HO" ? "HO" : "Branch",
          branchid: branchId || "8",
        },
        headers: commonHeaders,
      });

      const branches = Array.isArray(res.data?.branches)
        ? res.data.branches
        : Array.isArray(res.data)
        ? res.data
        : [];

      setBranchesList(branches);
    } catch (error) {
      toast.error("Failed to load branches");
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await axios.get(`${API_BASE}/supplierddl_rpt`, {
        headers: commonHeaders,
      });

      const suppliers = Array.isArray(res.data?.suppliers)
        ? res.data.suppliers
        : Array.isArray(res.data)
        ? res.data
        : [];

      setSuppliersList(suppliers);
    } catch (error) {
      toast.error("Failed to load suppliers");
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchSuppliers();
  }, [commonHeaders]);

  const handleSearch = async () => {
    if (!fromDate || !toDate) {
      toast.warning("Please select both from and to dates");
      return;
    }

    try {
      setLoading(true);

      const params = {
        bpno: bpNo || "%",
        supplier: supplier || "%",
        branch: branch || branchId || "8",
        fromdate: formatDateForApi(fromDate),
        todate: formatDateForApi(toDate),
      };

      const res = await axios.get(`${API_BASE}/billpass_rpt`, {
        params,
        headers: commonHeaders,
      });

      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setResults(data);
      setGrandTotal(Number(res.data?.grandTotal || 0));

      toast.success(`Loaded ${data.length} record(s)`);
    } catch (error) {
      toast.error("Failed to fetch Bill Pass report");
      setResults([]);
      setGrandTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const params = {
      bpno: bpNo || "%",
      supplier: supplier || "%",
      branch: branch || branchId || "8",
      from: fromDate ? formatDateForApi(fromDate).replace(/\//g, "-") : "",
      to: toDate ? formatDateForApi(toDate).replace(/\//g, "-") : "",
    };

    const query = new URLSearchParams(params).toString();
    window.open(`/inventory/HoBillPass_ReportPrint?${query}`, "_blank");
  };

  const handleViewRow = (item) => {
    const params = {
      bpno: item.fld_BillPassNo || "%",
      supplier: item.fld_supplierid || supplier || "%",
      branch: item.fld_Branchid || branch || branchId || "8",
      from: fromDate ? formatDateForApi(fromDate).replace(/\//g, "-") : "",
      to: toDate ? formatDateForApi(toDate).replace(/\//g, "-") : "",
    };

    const query = new URLSearchParams(params).toString();
    window.open(`/inventory/HoBillPass_ReportPrint?${query}`, "_blank");
  };

  return (
    <LayoutWrapper>
      <HeaderBar title="HO BILL PASS REPORT" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-4 flex-wrap">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              HO Bill Pass Report
            </h1>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark
                  ? "bg-blue-900 text-blue-100"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {results.length} Records
            </span>

            {results.length > 0 && (
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark
                    ? "bg-green-900 text-green-100"
                    : "bg-green-100 text-green-800"
                }`}
              >
                Grand Total: {formatCurrency(grandTotal)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-700" +
                    (showFilters ? " bg-gray-700" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-100" +
                    (showFilters ? " bg-gray-100" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={handleExport}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-700"
              }`}
            >
              <FaDownload className="w-4 h-4" />
              Export
            </button>

            <button
              onClick={handlePrint}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              <FaPrint className="w-4 h-4" />
              Print Report
            </button>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                Search Filters
              </h3>

              <button
                onClick={clearFilters}
                className={`text-sm flex items-center gap-2 ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                <FaTimes className="w-3 h-3" />
                Clear all
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  From Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  To Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Bill Pass No
                </label>
                <input
                  type="text"
                  value={bpNo}
                  onChange={(e) => setBpNo(e.target.value)}
                  placeholder="Enter bill pass number"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Branch
                </label>
                <select
                  value={branch}
                  onChange={(e) => setBranch(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="8">Select Branch</option>
                  {branchesList.map((b) => (
                    <option
                      key={b.fld_Branchid || b.fld_branchid}
                      value={b.fld_Branchid || b.fld_branchid}
                    >
                      {b.fld_BranchName || b.fld_branchname}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Supplier
                </label>
                <select
                  value={supplier}
                  onChange={(e) => setSupplier(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="">Select Supplier</option>
                  {suppliersList.map((s) => (
                    <option
                      key={s.fld_supplierid}
                      value={s.fld_supplierid}
                    >
                      {s.fld_suppliername}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              className={`flex justify-between items-center pt-4 border-t ${
                isDark ? "border-gray-600" : "border-gray-200"
              } flex-wrap gap-3`}
            >
              <button
                onClick={clearFilters}
                className={`px-4 py-2 font-medium ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Clear Filters
              </button>

              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
                >
                  <FaSearch className="w-4 h-4" />
                  Search
                </button>

                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded transition-colors"
                >
                  <FaPrint className="w-4 h-4" />
                  Print
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : results.length > 0 ? (
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center flex-wrap gap-2">
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                HO Bill Pass Results ({results.length})
              </h3>

              <div
                className={`text-sm font-semibold ${
                  isDark ? "text-green-300" : "text-green-700"
                }`}
              >
                Grand Total: {formatCurrency(grandTotal)}
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Sl No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Bill Pass No
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Supplier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Branch
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody
                  className={`divide-y divide-gray-200 ${
                    isDark ? "bg-gray-800" : "bg-white"
                  }`}
                >
                  {results.map((item, idx) => (
                    <tr
                      key={idx}
                      className={`transition-colors ${
                        isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                      }`}
                    >
                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {idx + 1}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-blue-600">
                        {item.fld_BillPassNo || "-"}
                      </td>

                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {item.fld_EntryDate || formatDate(item.fld_date)}
                      </td>

                      <td
                        className={`px-4 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {item.fld_suppliername || "-"}
                      </td>

                      <td
                        className={`px-4 py-4 text-sm ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {item.branchname || "-"}
                      </td>

                      <td
                        className={`px-4 py-4 whitespace-nowrap text-sm font-medium ${
                          isDark ? "text-gray-300" : "text-gray-900"
                        }`}
                      >
                        {formatCurrency(item.fld_TotalValueNew || item.fld_TotalValue)}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewRow(item)}
                            className={`p-2 rounded transition-colors ${
                              isDark
                                ? "text-blue-400 hover:text-blue-300 hover:bg-gray-700"
                                : "text-blue-600 hover:text-blue-800 hover:bg-gray-100"
                            }`}
                            title="View"
                          >
                            <FaEye className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleViewRow(item)}
                            className={`p-2 rounded transition-colors ${
                              isDark
                                ? "text-green-400 hover:text-green-300 hover:bg-gray-700"
                                : "text-green-600 hover:text-green-800 hover:bg-gray-100"
                            }`}
                            title="Print"
                          >
                            <FaFileInvoiceDollar className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot className={isDark ? "bg-gray-900" : "bg-gray-100"}>
                  <tr>
                    <td
                      colSpan="5"
                      className={`px-4 py-3 text-right text-sm font-bold ${
                        isDark ? "text-white" : "text-gray-900"
                      }`}
                    >
                      Grand Total
                    </td>
                    <td
                      className={`px-4 py-3 text-sm font-bold ${
                        isDark ? "text-green-300" : "text-green-700"
                      }`}
                    >
                      {formatCurrency(grandTotal)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : (
          showFilters && (
            <div
              className={`text-center py-12 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <p>No records found. Please use the filters to search bill pass records.</p>
            </div>
          )
        )}
      </div>
    </LayoutWrapper>
  );
};

export default HOBillPassReport;